import React, { useEffect, useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from '@mui/material';
import {
  Save as SaveIcon,
  Clear as ClearIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { pedidoService, Pedido, PedidoRequest, ProdutoPedido, PagamentoPedido } from '../../services/pedido';

// Temporary interfaces to match backend structure
interface BackendProdutoPedido {
  id?: number;
  nomeProduto: string;
  precoCompra: number;
  referencia: string;
  quantidade: number;
  grade: string;
  dataEntrega: null;
}

interface BackendPagamentoPedido {
  id?: number;
  idFormaPagamento: number;
  formaPagamento: string;
  valorPago: number;
  dataPagamento: string;
}
import { fornecedorService, Fornecedor } from '../../services/fornecedor';
import DatePickerField from '../common/DatePickerField';

interface PedidoFormProps {
  isEdit?: boolean;
  isView?: boolean;
}

const PedidoForm: React.FC<PedidoFormProps> = ({ isEdit = false, isView = false }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  
  const [formData, setFormData] = useState({
    id: 0,
    idFornecedor: 0,
    dataDoPedido: '',
    dataPrevistaEntrega: '',
    valorPedido: 0,
    valorFrete: 0,
    tipoFrete: '',
  });
  
  const [errors, setErrors] = useState({
    idFornecedor: '',
    dataDoPedido: '',
    dataPrevistaEntrega: '',
    valorPedido: '',
    tipoFrete: '',
  });

  const [produtos, setProdutos] = useState<ProdutoPedido[]>([
    {
      id: 0,
      idPedido: 0,
      idProduto: 0,
      nomeProduto: '',
      referencia: '',
      quantidade: 0,
      valorUnitario: 0,
      valorTotal: 0,
      grade: '',
    }
  ]);
  
  const [pagamentos, setPagamentos] = useState<PagamentoPedido[]>([
    {
      id: 0,
      idPedido: 0,
      formaPagamento: '',
      valor: 0,
      dataPagamento: '',
    }
  ]);

  // Mock payment methods
  const formasPagamento = [
    { id: 1, name: 'Dinheiro' },
    { id: 2, name: 'Cartão de Crédito' },
    { id: 3, name: 'Cartão de Débito' },
    { id: 4, name: 'Transferência Bancária' },
    { id: 5, name: 'Boleto' },
  ];

  // Buscar fornecedores para o select
  const { data: fornecedores = [] } = useQuery({
    queryKey: ['fornecedores'],
    queryFn: fornecedorService.getAll,
  });

  // Buscar pedido para edição
  const { data: pedido, isLoading } = useQuery({
    queryKey: ['pedido', id],
    queryFn: () => pedidoService.getById(Number(id)),
    enabled: isEdit && !!id,
  });

  // Mutation para salvar
  const saveMutation = useMutation({
    mutationFn: (data: { pedido: PedidoRequest; produtos: ProdutoPedido[]; pagamentos: PagamentoPedido[] }) => 
      isEdit ? pedidoService.update(data.pedido, data.produtos, data.pagamentos) : pedidoService.save(data.pedido, data.produtos, data.pagamentos),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pedidos'] });
      navigate('/pedidos');
    },
    onError: (error: any) => {
      console.error('Erro ao salvar pedido:', error);
    },
  });

  useEffect(() => {
    if (pedido && isEdit) {
      setFormData({
        id: pedido.id || 0,
        idFornecedor: pedido.idFornecedor || 0,
        dataDoPedido: pedido.dataDoPedido || '',
        dataPrevistaEntrega: pedido.dataPrevistaEntrega || '',
        valorPedido: pedido.valorPedido || 0,
        valorFrete: pedido.valorFrete || 0,
        tipoFrete: pedido.tipoFrete || '',
      });
    }
  }, [pedido, isEdit]);

  // Product management functions
  const addProduto = () => {
    setProdutos([...produtos, {
      id: 0,
      idPedido: 0,
      idProduto: 0,
      nomeProduto: '',
      referencia: '',
      quantidade: 0,
      valorUnitario: 0,
      valorTotal: 0,
      grade: '',
    }]);
  };

  const removeProduto = (index: number) => {
    const newProdutos = produtos.filter((_, i) => i !== index);
    setProdutos(newProdutos);
    calcularTotalPedido();
  };

  const updateProduto = (index: number, field: string, value: any) => {
    const newProdutos = [...produtos];
    
    // Handle numeric fields properly
    if (field === 'quantidade' || field === 'valorUnitario') {
      const numValue = value === '' || value === null || value === undefined ? 0 : Number(value);
      newProdutos[index] = { ...newProdutos[index], [field]: numValue };
      
      // Calculate total when quantity or unit price changes
      newProdutos[index].valorTotal = newProdutos[index].quantidade * newProdutos[index].valorUnitario;
    } else {
      newProdutos[index] = { ...newProdutos[index], [field]: value };
    }
    
    setProdutos(newProdutos);
    calcularTotalPedido();
  };

  const calcularTotalPedido = () => {
    const total = produtos.reduce((sum, produto) => sum + produto.valorTotal, 0);
    setFormData(prev => ({ ...prev, valorPedido: total }));
  };

  // Payment management functions
  const addPagamento = () => {
    setPagamentos([...pagamentos, {
      id: 0,
      idPedido: 0,
      formaPagamento: '',
      valor: 0,
      dataPagamento: '',
    }]);
  };

  const removePagamento = (index: number) => {
    setPagamentos(pagamentos.filter((_, i) => i !== index));
  };

  const updatePagamento = (index: number, field: string, value: any) => {
    const newPagamentos = [...pagamentos];
    
    // Handle numeric fields properly
    if (field === 'valor') {
      const numValue = value === '' || value === null || value === undefined ? 0 : Number(value);
      newPagamentos[index] = { ...newPagamentos[index], [field]: numValue };
    } else {
      newPagamentos[index] = { ...newPagamentos[index], [field]: value };
    }
    
    setPagamentos(newPagamentos);
  };

  const validateForm = (): boolean => {
    const newErrors = {
      idFornecedor: '',
      dataDoPedido: '',
      dataPrevistaEntrega: '',
      valorPedido: '',
      tipoFrete: '',
    };
    let isValid = true;

    if (!formData.idFornecedor) {
      newErrors.idFornecedor = 'Fornecedor é obrigatório';
      isValid = false;
    }

    if (!formData.dataDoPedido) {
      newErrors.dataDoPedido = 'Data do pedido é obrigatória';
      isValid = false;
    }

    if (!formData.dataPrevistaEntrega) {
      newErrors.dataPrevistaEntrega = 'Data prevista de entrega é obrigatória';
      isValid = false;
    }

    if (!formData.valorPedido || formData.valorPedido <= 0) {
      newErrors.valorPedido = 'Valor do pedido deve ser maior que zero';
      isValid = false;
    }

    if (!formData.tipoFrete) {
      newErrors.tipoFrete = 'Tipo de frete é obrigatório';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement | { value: unknown }> | any) => {
    const value = e.target?.value || e;
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Convert dates from dd/MM/yyyy to ISO format
      const convertToISODate = (dateString: string): string => {
        if (!dateString) return '';
        const [day, month, year] = dateString.split('/');
        return `${year}-${month}-${day}T00:00:00`;
      };

      // Filter out empty products and map to backend structure
      const filteredProdutos: BackendProdutoPedido[] = produtos
        .filter(produto => 
          produto.nomeProduto.trim() !== '' || 
          produto.referencia.trim() !== '' || 
          produto.quantidade > 0
        )
        .map(produto => ({
          id: produto.id,
          nomeProduto: produto.nomeProduto,
          precoCompra: produto.valorUnitario,
          referencia: produto.referencia,
          quantidade: produto.quantidade,
          grade: produto.grade,
          dataEntrega: null // DateTime? nullable
        }));

      // Filter out empty payments and map to backend structure
      const filteredPagamentos: BackendPagamentoPedido[] = pagamentos
        .filter(pagamento => 
          pagamento.formaPagamento.trim() !== '' || 
          pagamento.valor > 0 || 
          pagamento.dataPagamento.trim() !== ''
        )
        .map(pagamento => {
          // Find payment method ID from formasPagamento array
          const formaPagamentoObj = formasPagamento.find(f => f.name === pagamento.formaPagamento);
          return {
            id: pagamento.id,
            idFormaPagamento: formaPagamentoObj ? formaPagamentoObj.id : 0,
            formaPagamento: pagamento.formaPagamento,
            valorPago: pagamento.valor,
            dataPagamento: convertToISODate(pagamento.dataPagamento)
          };
        });

      const pedidoData: PedidoRequest = {
        id: isEdit ? Number(id) : undefined,
        idFornecedor: formData.idFornecedor,
        dataDoPedido: convertToISODate(formData.dataDoPedido),
        dataPrevistaEntrega: convertToISODate(formData.dataPrevistaEntrega),
        valorPedido: formData.valorPedido,
        valorFrete: formData.valorFrete,
        tipoFrete: formData.tipoFrete,
      };
      
      saveMutation.mutate({
        pedido: pedidoData,
        produtos: filteredProdutos as any,
        pagamentos: filteredPagamentos as any,
      });
    }
  };

  const handleClear = () => {
    setFormData({
      id: 0,
      idFornecedor: 0,
      dataDoPedido: '',
      dataPrevistaEntrega: '',
      valorPedido: 0,
      valorFrete: 0,
      tipoFrete: '',
    });
    setProdutos([]);
    setPagamentos([]);
    setErrors({
      idFornecedor: '',
      dataDoPedido: '',
      dataPrevistaEntrega: '',
      valorPedido: '',
      tipoFrete: '',
    });
  };

  const handleBack = () => {
    navigate('/pedidos');
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography>Carregando...</Typography>
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box component="form" onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Header section - similar to Vue version */}
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center' }}>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <FormControl fullWidth error={!!errors.tipoFrete}>
                <InputLabel>Tipo de Frete</InputLabel>
                <Select
                  value={formData.tipoFrete}
                  onChange={handleInputChange('tipoFrete')}
                  disabled={isView}
                >
                  <MenuItem value="CIF">CIF</MenuItem>
                  <MenuItem value="FOB">FOB</MenuItem>
                </Select>
                {errors.tipoFrete && (
                  <Typography color="error" variant="caption">
                    {errors.tipoFrete}
                  </Typography>
                )}
              </FormControl>
            </Box>

            <Box sx={{ minWidth: 150 }}>
              <TextField
                fullWidth
                label="Valor do Frete"
                type="number"
                value={formData.valorFrete}
                onChange={handleInputChange('valorFrete')}
                slotProps={{
                  input: {
                    startAdornment: 'R$ ',
                  }
                }}
                disabled={isView}
              />
            </Box>

            <Box sx={{ minWidth: 200 }}>
              <DatePickerField
                label="Data Compra"
                value={formData.dataDoPedido}
                onChange={handleInputChange('dataDoPedido')}
                disabled={isView}
                required
              />
            </Box>

            <Box sx={{ minWidth: 200 }}>
              <DatePickerField
                label="Data Entrega"
                value={formData.dataPrevistaEntrega}
                onChange={handleInputChange('dataPrevistaEntrega')}
                disabled={isView}
                required
              />
            </Box>

            <Box sx={{ minWidth: 200 }}>
              <TextField
                fullWidth
                label="Valor do Pedido"
                type="number"
                value={formData.valorPedido}
                onChange={handleInputChange('valorPedido')}
                error={!!errors.valorPedido}
                helperText={errors.valorPedido}
                slotProps={{
                  input: {
                    startAdornment: 'R$ ',
                    readOnly: true, // Calculated from products
                  }
                }}
                disabled={isView}
                required
              />
            </Box>
          </Box>

          {/* Supplier selection */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: 1, minWidth: 300 }}>
              <FormControl fullWidth error={!!errors.idFornecedor}>
                <InputLabel>Fornecedor</InputLabel>
                <Select
                  value={formData.idFornecedor}
                  onChange={handleInputChange('idFornecedor')}
                  disabled={isView}
                >
                  {fornecedores.map((fornecedor) => (
                    <MenuItem key={fornecedor.id} value={fornecedor.id}>
                      {fornecedor.nome}
                    </MenuItem>
                  ))}
                </Select>
                {errors.idFornecedor && (
                  <Typography color="error" variant="caption">
                    {errors.idFornecedor}
                  </Typography>
                )}
              </FormControl>
            </Box>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={addProduto}
              disabled={isView}
              sx={{ height: '56px' }}
            >
              Adicionar Produto
            </Button>
          </Box>

          {/* Products Table */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>Referência</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 200 }}>Nome do Produto</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>Quantidade</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>Preço Compra</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>Total</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 200 }}>Grade</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 80 }}>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {produtos.map((produto, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <TextField
                        size="small"
                        value={produto.referencia}
                        onChange={(e) => updateProduto(index, 'referencia', e.target.value)}
                        disabled={isView}
                        sx={{ minWidth: 80 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={produto.nomeProduto}
                        onChange={(e) => updateProduto(index, 'nomeProduto', e.target.value)}
                        disabled={isView}
                        sx={{ minWidth: 180 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={produto.quantidade}
                        onChange={(e) => updateProduto(index, 'quantidade', Number(e.target.value))}
                        disabled={isView}
                        sx={{ minWidth: 80 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={produto.valorUnitario}
                        onChange={(e) => updateProduto(index, 'valorUnitario', Number(e.target.value))}
                        disabled={isView}
                        slotProps={{
                          input: {
                            startAdornment: 'R$ ',
                          }
                        }}
                        sx={{ minWidth: 100 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={produto.valorTotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        disabled={isView}
                        slotProps={{
                          htmlInput: {
                            readOnly: true,
                          },
                        }}
                        sx={{ minWidth: 100 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={produto.grade}
                        onChange={(e) => updateProduto(index, 'grade', e.target.value)}
                        disabled={isView}
                        placeholder="P:10; M:10; G:10"
                        sx={{ minWidth: 180 }}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => removeProduto(index)}
                        disabled={isView}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Payment Section */}
          <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
            Pagamentos
          </Typography>
          
          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>Forma de Pagamento</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>Valor</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>Data Pagamento</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 80 }}>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagamentos.map((pagamento, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <FormControl fullWidth size="small">
                        <Select
                          value={pagamento.formaPagamento}
                          onChange={(e) => updatePagamento(index, 'formaPagamento', e.target.value)}
                          disabled={isView}
                        >
                          {formasPagamento.map((forma) => (
                            <MenuItem key={forma.id} value={forma.name}>
                              {forma.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={pagamento.valor}
                        onChange={(e) => updatePagamento(index, 'valor', Number(e.target.value))}
                        disabled={isView}
                        slotProps={{
                          input: {
                            startAdornment: 'R$ ',
                          }
                        }}
                        sx={{ minWidth: 100 }}
                      />
                    </TableCell>
                    <TableCell>
                      <DatePickerField
                        label="Data Pagamento"
                        value={pagamento.dataPagamento}
                        onChange={(value) => updatePagamento(index, 'dataPagamento', value)}
                        disabled={isView}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => removePagamento(index)}
                        disabled={isView}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={addPagamento}
              disabled={isView}
              sx={{ height: '40px' }}
            >
              Adicionar Pagamento
            </Button>
          </Box>
          
          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 3 }}>
            {!isView && (
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={saveMutation.isPending}
                sx={{ minWidth: 120 }}
              >
                {saveMutation.isPending ? 'Salvando...' : 'Salvar'}
              </Button>
            )}
            
            {!isView && (
              <Button
                type="button"
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={handleClear}
                sx={{ minWidth: 120 }}
              >
                Limpar
              </Button>
            )}
            
            <Button
              type="button"
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              sx={{ minWidth: 120 }}
            >
              Voltar
            </Button>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default PedidoForm;
